<template>
    <div class="ib" :class="{ 'ib-error': errorField }">
        <div class="ib-head">
            <span class="ib-title">Factura a</span>
            <SelectButton :modelValue="modelValue.mode" @update:modelValue="setMode" :options="modes"
                          optionLabel="label" optionValue="value" optionDisabled="disabled" :unselectable="false"
                          class="ib-modes" aria-label="A nombre de quién va la factura" />
        </div>
        <small v-if="!finalAllowed && limitHint" class="ib-hint">
            <i class="pi pi-info-circle"></i> Desde ${{ limitText }} la factura lleva los datos del cliente (SRI).
        </small>

        <template v-if="modelValue.mode === 'data'">
            <div class="p-inputgroup">
                <InputText :modelValue="modelValue.identification" @update:modelValue="onIdentification"
                           placeholder="Cédula, RUC o pasaporte" inputmode="numeric" maxlength="20"
                           :class="{ 'p-invalid': errorField === 'identification' }" aria-label="Cédula, RUC o pasaporte"
                           @keydown.enter.prevent="find" />
                <Button icon="pi pi-search" :loading="searching" aria-label="Buscar cliente" @click="find" />
            </div>
            <small v-if="lookup === 'found'" class="ib-found"><i class="pi pi-check-circle"></i> Cliente encontrado</small>
            <small v-else-if="lookup === 'new'" class="ib-new">Cliente nuevo: completa sus datos (se guardan al facturar).</small>

            <InputText :modelValue="modelValue.name" @update:modelValue="v => set('name', v)"
                       placeholder="Nombre o razón social *" maxlength="300"
                       :class="{ 'p-invalid': errorField === 'name' }" aria-label="Nombre o razón social" />
            <InputText :modelValue="modelValue.email" @update:modelValue="v => set('email', v)" type="email"
                       placeholder="Correo (se le envía la factura)" maxlength="200"
                       :class="{ 'p-invalid': errorField === 'email' }" aria-label="Correo" />
            <div class="ib-two">
                <InputText :modelValue="modelValue.phoneNumber" @update:modelValue="v => set('phoneNumber', v)"
                           placeholder="Teléfono" inputmode="tel" maxlength="20" aria-label="Teléfono" />
                <InputText :modelValue="modelValue.address" @update:modelValue="v => set('address', v)"
                           placeholder="Dirección" maxlength="300" aria-label="Dirección" />
            </div>
        </template>
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import { finalConsumerAllowed, isLookupReady, DEFAULT_FINAL_CONSUMER_LIMIT } from '../../service/restaurantPayment';

export default {
    name: 'InvoiceBuyerForm',
    props: {
        modelValue: { type: Object, required: true },   // {mode, id, identification, name, email, phoneNumber, address}
        total: { type: Number, default: 0 },
        limit: { type: Number, default: DEFAULT_FINAL_CONSUMER_LIMIT },
        errorField: { type: String, default: null },      // campo que el servidor/validación marcó
        // Busca al cliente por identificación: `{status, data: cliente | null}`. Mesas usa el de
        // restaurante (pide `restaurantTables`); Ventas pasa el suyo.
        findBuyer: { type: Function, default: (identification) => httpRequest.findRestaurantBuyer(identification) },
        limitHint: { type: Boolean, default: true },     // false: quien lo usa ya muestra el aviso del tope
    },
    emits: ['update:modelValue'],
    data() {
        return { searching: false, lookup: null, lastLookup: null };
    },
    computed: {
        finalAllowed() { return finalConsumerAllowed(this.total, this.limit); },
        limitText() { return Number(this.limit).toFixed(0); },
        modes() {
            return [
                { label: 'Consumidor final', value: 'final', disabled: !this.finalAllowed },
                { label: 'Con datos', value: 'data', disabled: false },
            ];
        },
    },
    watch: {
        // Si la cuenta sube a $50 o más con «consumidor final» elegido, pasa a pedir datos.
        finalAllowed(allowed) {
            if (!allowed && this.modelValue.mode === 'final') this.setMode('data');
        },
    },
    methods: {
        update(patch) {
            this.$emit('update:modelValue', { ...this.modelValue, ...patch });
        },
        set(key, value) { this.update({ [key]: value }); },
        setMode(mode) { this.update({ mode }); },
        onIdentification(value) {
            // Otra identificación = otro cliente: suelta el id del que se había encontrado.
            this.update({ identification: value, id: null });
            this.lookup = null;
            // Con el valor recién tipeado: la prop todavía trae el anterior hasta el próximo render.
            if (isLookupReady(value) && value !== this.lastLookup) this.find(value);
        },
        async find(typed) {
            const identification = (typeof typed === 'string' ? typed : this.modelValue.identification || '').trim();
            if (!identification || this.searching) return;
            this.searching = true;
            this.lastLookup = identification;
            const res = await this.findBuyer(identification);
            this.searching = false;
            if (!res || res.status !== 200) return;
            if (res.data) {
                const c = res.data;
                this.update({ id: c.id, identification: c.identification, name: c.name || '',
                              email: c.email || '', phoneNumber: c.phoneNumber || '', address: c.address || '' });
                this.lookup = 'found';
            } else {
                this.lookup = 'new';
            }
        },
    },
};
</script>

<style scoped>
.ib { display: flex; flex-direction: column; gap: .45rem; padding: .7rem; border: 1px solid var(--surface-border);
    border-radius: 10px; background: var(--surface-50); }
.ib-error { border-color: #f87171; }
.ib-head { display: flex; align-items: center; justify-content: space-between; gap: .5rem; flex-wrap: wrap; }
.ib-title { font-weight: 700; font-size: .9rem; }
.ib-modes :deep(.p-button) { padding: .35rem .6rem; font-size: .8rem; }
.ib-hint { color: var(--text-color-secondary); }
.ib-found { color: #15803d; }
.ib-new { color: var(--text-color-secondary); }
.ib-two { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr); gap: .45rem; }
.ib :deep(.p-inputtext) { width: 100%; min-width: 0; }
</style>
